import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  paginationRange,
} from '@mdvip/shared/components/ui/pagination';
import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { DatasetState } from '@/hooks/member-views/useMemberDataset';
import type { MemberRowData, MemberTable } from '@/lib/member-views/memberDataset.types';

export interface DirectoryPaginationProps<TRow extends MemberRowData> {
  table: MemberTable<TRow>;
  state: DatasetState;
}

/**
 * DirectoryPagination — Previous, numbered pages with ellipses, Next.
 *
 * Hidden while the view loads, when it failed, and when everything fits on one page.
 */
export function DirectoryPagination<TRow extends MemberRowData>({ table, state }: DirectoryPaginationProps<TRow>) {
  const { t } = useTranslation('shared');

  if (state === 'loading' || state === 'error') return null;

  return (
    <table.Subscribe selector={(tableState) => tableState.pagination}>
      {({ pageIndex }) => {
        const pageCount = table.getPageCount();
        if (pageCount <= 1) return null;

        const page = pageIndex + 1;
        const goTo = (target: number) => (e: React.MouseEvent) => {
          e.preventDefault();
          // A "disabled" Previous/Next is only pointer-events-none/tabIndex=-1, which doesn't block a
          // screen reader's synthetic activation or a direct .click(), so this guard is the backstop.
          if (target < 1 || target > pageCount) return;
          table.setPageIndex(target - 1);
        };

        return (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  disabled={page === 1}
                  onClick={goTo(page - 1)}
                  label={t('contentListing.previous')}
                />
              </PaginationItem>
              {paginationRange(page, pageCount).map((item) =>
                typeof item === 'number' ? (
                  <PaginationItem key={item}>
                    <PaginationLink href="#" isActive={item === page} onClick={goTo(item)}>
                      {item}
                    </PaginationLink>
                  </PaginationItem>
                ) : (
                  <PaginationItem key={item}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ),
              )}
              <PaginationItem>
                <PaginationNext
                  href="#"
                  disabled={page === pageCount}
                  onClick={goTo(page + 1)}
                  label={t('contentListing.next')}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        );
      }}
    </table.Subscribe>
  );
}
